import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import axios from "axios";
import { useAlertContext } from "./AlertContext";
import {
  API_CREATE_MODEL_URI,
  API_GET_MODELS_URI,
  API_UPDATE_MODEL_URI,
  DEFAULT_CONFIG,
  USER_PROFILE_MODEL,
  USER_PROFILE_FIELDS
} from "./Constants";
import { clearProfilePersistence } from "../utils/profilePersistence";

const AuthContext = createContext({
  submitting: false,
  fetching: false,
  loggedIn: false,
  user: null,
  profile: null,
  userVerify: false,
  userAttributes: [],
  login: async () => {},
  register: async () => {},
  confirmSignup: async () => {},
  resetPassword: async () => {},
  forgotPassword: async () => {},
  setUser: async () => {},
  logout: async () => {},
  fetchUserAttributes: async () => {},
  fetchCurrentUser: async () => {},
  refreshSession: async () => {},
  retryCount: 0,
  createOdooProfile: async () => {},
  updateOdooProfile: async () => {},
  searchOdooProfile: async () => {}
});

const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userVerify, setUserVerify] = useState(false);
  const [userAttributes, setUserAttributes] = useState([]);
  const [loggedIn, setLoggedIn] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [requestInterceptorIds, setRequestInterceptorIds] = useState([]);
  const { setError } = useAlertContext();

  // useEffect(() => {
  //   fetchCurrentUser().then(async () => {
  //     // await fetchUserAttributes();
  //   });
  // }, []);
  useEffect(() => {
    fetchCurrentUser(); // Initial check

    const interval = setInterval(() => {
      fetchCurrentUser();
    }, 5 * 60 * 1000); // every 5 minutes

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user || !user.getSignInUserSession()) return;

    // Add request interceptor
    registerRequestInterceptor(user);

    // Add response interceptor for 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config;
        if (
          error.response &&
          error.response.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshSession();
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: newToken
            };
            return axios(originalRequest);
          } catch (refreshError) {
            if (!sessionStorage.getItem("sessionExpired")) {
              sessionStorage.setItem("sessionExpired", "1");
              const Swal = (await import("sweetalert2")).default;
              await Swal.fire({
                title: "Session Expired",
                text: "Your session has expired. Please log in again.",
                icon: "warning",
                confirmButtonText: "Login",
                customClass: {
                  confirmButton: "my-confirm-button"
                }
              });
            }
            await logout();
            window.location.href = "/login";
            return;
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  const registerRequestInterceptor = (u) => {
    // Remove previous interceptor
    requestInterceptorIds.forEach((id) => axios.interceptors.request.eject(id));
    // Request interceptor
    const id = axios.interceptors.request.use((config) => {
      if (u.getSignInUserSession() !== null) {
        config.headers.Authorization = u.getSignInUserSession().getIdToken().jwtToken;
      }
      return config;
    });
    setRequestInterceptorIds((prev) => [...prev, id]);
  };

  useEffect(() => {
    if (user && user.getSignInUserSession()) {
      registerRequestInterceptor(user);
      // Response interceptor
      // axios.interceptors.response.use((resp) => {
      //   if (resp.statusCode === 401) {
      //     refreshSession();
      //   }
      //   return resp;
      // });
      setLoggedIn(true);
      setUserVerify(!!user.attributes?.email_verified);
    } else {
      setLoggedIn(false);
      setUserVerify(false);
    }
  }, [user]);

  const searchOdooProfile = useCallback(async (email) => {
    if (!email) {
      setProfile(null);
      return null;
    }

    try {
      const resp = await axios.post(API_GET_MODELS_URI, {
        configName: DEFAULT_CONFIG,
        model: USER_PROFILE_MODEL,
        fields: [["email", "=", email]],
        outputrecords: { fields: USER_PROFILE_FIELDS, limit: 1 }
      });
      const profileData = Array.isArray(resp.data) && resp.data.length > 0 ? resp.data[0] : null;
      setProfile(profileData);
      return profileData;
    } catch (e) {
      setProfile(null);
      throw e;
    }
  }, []);

  const updateOdooProfile = useCallback(
    async (id, payload) => {
      const resp = await axios.post(API_UPDATE_MODEL_URI, {
        configName: DEFAULT_CONFIG,
        model: USER_PROFILE_MODEL,
        input: payload,
        id
      });

      const email = payload.email || user?.attributes?.email;
      if (email) {
        await searchOdooProfile(email);
      } else if (profile) {
        setProfile({ ...profile, ...payload, id });
      }

      return resp.data;
    },
    [profile, searchOdooProfile, user]
  );

  const createOdooProfile = useCallback(
    async (payload) => {
      const resp = await axios
        .post(API_CREATE_MODEL_URI, {
          configName: DEFAULT_CONFIG,
          model: USER_PROFILE_MODEL,
          input: payload
        })
        .then((resp) => resp.data);

      await Auth.updateUserAttributes(user, {
        "custom:CREATION-DATE": new Date(),
        "custom:ORIGINATING-APP": "R3API",
        "custom:PROFILE-CRM": "odoo"
      });

      if (payload.email) {
        await searchOdooProfile(payload.email);
      }

      return resp;
    },
    [searchOdooProfile, user]
  );

  const refreshSession = useCallback(() => {
    return new Promise((resolve, reject) => {
      Auth.currentAuthenticatedUser()
        .then((currentUser) => {
          const currentSession = currentUser.signInUserSession;
          currentUser.refreshSession(currentSession.refreshToken, (err, session) => {
            if (err) {
              setRetryCount((prev) => prev + 1);
              reject(err);
              return;
            }
            setUser(currentUser);
            registerRequestInterceptor(currentUser);
            resolve(session.getIdToken().jwtToken);
          });
        })
        .catch((e) => {
          setRetryCount((prev) => prev + 1);
          reject(e);
        });
    });
  }, []);

  const loadProfileForUser = useCallback(
    async (email) => {
      try {
        await searchOdooProfile(email);
      } catch (e) {
        if (e?.response?.status === 401) {
          await refreshSession();
          await searchOdooProfile(email);
          return;
        }
        throw e;
      }
    },
    [searchOdooProfile, refreshSession]
  );

  const fetchCurrentUser = useCallback(async () => {
    if (user) {
      return user;
    }
    setFetching(true);
    let u = null;
    try {
      u = await Auth.currentAuthenticatedUser();
      registerRequestInterceptor(u);
      await loadProfileForUser(u.attributes.email);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : typeof e === "string" ? e : "";
      console.error("Failed to restore session", errorMsg);
    } finally {
      setUser(u);
      sessionStorage.removeItem("sessionExpired");
      setFetching(false);
    }
    return u;
  }, [user, loadProfileForUser]);
  // const fetchCurrentUser = useCallback(async () => {
  //   const publicPaths = ["/login", "/register", "/forgot-password", "/verify"];
  //   const currentPath = window.location.pathname;

  //   // ✅ Step 1: If on public path, skip session check completely
  //   if (publicPaths.includes(currentPath)) {
  //     setFetching(false);
  //     return null;
  //   }

  //   setFetching(true);
  //   let u = null;

  //   try {
  //     u = await Auth.currentAuthenticatedUser();
  //     registerRequestInterceptor(u);
  //     await searchOdooProfile(u.attributes.email);
  //   } catch (e) {
  //     const errorMsg = e instanceof Error ? e.message : typeof e === "string" ? e : "";

  //     if (
  //       errorMsg.includes("not authenticated") ||
  //       errorMsg.includes("access token expired") ||
  //       errorMsg.includes("No current user") ||
  //       errorMsg.includes("User is not authenticated") ||
  //       errorMsg.includes("The user is not authenticated")
  //     ) {
  //       if (!hasSessionPopupShown()) {
  //         markSessionPopupShown();
  //         const Swal = (await import("sweetalert2")).default;
  //         await Swal.fire({
  //           title: "Session Expired",
  //           text: "Your session has expired. Please login again.",
  //           icon: "warning",
  //           confirmButtonText: "OK"
  //         });
  //         await logout();
  //         window.location.href = "/login";
  //       }
  //       return;
  //     }

  //     setError(errorMsg);
  //     throw new Error(errorMsg);
  //   } finally {
  //     setUser(u);
  //     setFetching(false);
  //   }

  //   return u;
  // }, [user]);
  const login = async ({ username, password }) => {
    setSubmitting(true);
    setFetching(true);
    try {
      const u = await Auth.signIn(username, password);
      setUser(u);
      sessionStorage.removeItem("sessionExpired");
      registerRequestInterceptor(u);
      await searchOdooProfile(u.attributes.email);
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSubmitting(false);
      setFetching(false);
    }
  };

  const logout = async () => {
    setSubmitting(true);
    try {
      await Auth.signOut();
      setUser(null);
      setProfile(null);
      setUserAttributes([]);
      clearProfilePersistence();
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUserAttributes = useCallback(async () => {
    let attributes = [];
    if (user) {
      try {
        attributes = await Auth.userAttributes(user);
      } catch (e) {
        console.log(e);
        if (e.code === "UserNotFoundException") {
          await logout();
        }
      }
    }
    setUserAttributes(attributes);
    return attributes;
  }, [user, logout]);

  const forgotPassword = async (email) => {
    setSubmitting(true);
    try {
      await Auth.forgotPassword(email);
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (username, code, password) => {
    setSubmitting(true);
    try {
      await Auth.forgotPasswordSubmit(username, code, password);
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const register = async ({ username, password }) => {
    try {
      setSubmitting(true);
      const { user } = await Auth.signUp(username, password);
      setUserVerify(false);
      setUser(user);
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSignup = async (username, code) => {
    try {
      setSubmitting(true);
      await Auth.confirmSignUp(username, code);
      // setUserVerify(true);
    } catch (e) {
      setError(e.message);
      throw new Error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        fetching,
        user,
        profile,
        userVerify,
        submitting,
        userAttributes,
        login,
        logout,
        fetchUserAttributes,
        setUser,
        retryCount,
        refreshSession,
        fetchCurrentUser,
        forgotPassword,
        resetPassword,
        loggedIn,
        register,
        confirmSignup,
        createOdooProfile,
        updateOdooProfile,
        searchOdooProfile
      }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.element.isRequired
};

const useAuthContext = () => useContext(AuthContext);

export { AuthContextProvider, AuthContext, useAuthContext };
